// src/App.js
import { CssBaseline, ThemeProvider } from '@mui/material';
import { jwtDecode } from 'jwt-decode';
import React, { useEffect, useState } from 'react';
//import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useRoutes } from 'react-router-dom';


import RTL from './layouts/full-layout/customizer/RTL';
import ThemeSettings from './layouts/full-layout/customizer/ThemeSettings';
import 'react-perfect-scrollbar/dist/css/styles.css';

import Router from './routes/Router';
import { UserProvider } from './context/UserContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import { ConfirmProvider } from './components/common/ConfirmDialog';
import MaintenancePage from './components/common/MaintenancePage';
import { onMaintenance } from './api/apiClient';
import { MissingScopeHandler } from './components/integrations/MissingScopeHandler';

/**
 * Detect Shopify post-install redirect and continue OAuth flow.
 *
 * When using Shopify-managed install (admin.shopify.com/oauth/install),
 * Shopify redirects to App URL with shop/hmac/host/timestamp but NO code.
 * This hook detects that and redirects to backend to complete OAuth.
 */
function useShopifyInstallRedirect() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shop = params.get('shop');
    const hmac = params.get('hmac');
    const host = params.get('host');
    const code = params.get('code');
    const timestamp = params.get('timestamp');

    // Check if this is a Shopify post-install redirect:
    // - Has shop and hmac (Shopify signature)
    // - Does NOT have code (not an OAuth callback)
    // - Has host or timestamp (additional Shopify params)
    if (shop && hmac && !code && (host || timestamp)) {
      console.log('Detected Shopify post-install redirect, completing OAuth...');

      // Get client_id from JWT token in localStorage
      const token = localStorage.getItem('token');
      let clientId = null;
      if (token) {
        try {
          const decoded = jwtDecode(token);
          clientId = decoded?.client?.id;
        } catch (e) {
          console.error('Error decoding JWT token:', e);
        }
      }

      // Build redirect URL to complete-install endpoint
      const backendUrl = process.env.REACT_APP_API_URL || 'https://app.getisi.ai/api';
      const baseUrl = backendUrl.replace('/api', '');

      // Preserve Shopify's parameters and add client_id
      const redirectParams = new URLSearchParams();
      redirectParams.set('shop', shop);
      redirectParams.set('hmac', hmac);
      if (host) redirectParams.set('host', host);
      if (timestamp) redirectParams.set('timestamp', timestamp);
      if (clientId) redirectParams.set('client_id', clientId);

      const completeInstallUrl = `${baseUrl}/api/shopify/complete-install?${redirectParams.toString()}`;

      console.log('Redirecting to:', completeInstallUrl);
      window.location.href = completeInstallUrl;
    }
  }, []);
}

function App() {
  // Handle Shopify post-install redirects
  useShopifyInstallRedirect();

  const [isMaintenance, setIsMaintenance] = useState(false);
  const routing = useRoutes(Router);
  const theme = ThemeSettings();
  const customizer = useSelector((state) => state.CustomizerReducer);

  useEffect(() => {
    onMaintenance(() => setIsMaintenance(true));
  }, []);

  if (isMaintenance) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MaintenancePage />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <RTL direction={customizer.activeDir}>
        <ErrorBoundary>
          <ConfirmProvider>
            <UserProvider>
              <CssBaseline />
              <MissingScopeHandler />
              {routing}
            </UserProvider>
          </ConfirmProvider>
        </ErrorBoundary>
      </RTL>
    </ThemeProvider>
  );
}

export default App;
